import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PhoneField } from '../../shared/ui/PhoneField';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { Turnstile } from '../../shared/ui/Turnstile';

import { requestInstitute, type RequestInstituteResult } from './api';
import s from './RegisterInstitutePage.module.css';
import { requestInstituteSchema, type RequestInstituteInput } from './schemas';

export function RegisterInstitutePage(): JSX.Element {
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RequestInstituteResult | null>(null);
  // Null until the widget solves, and null again when a token expires. The
  // server treats a missing token as "not verified" only when a secret is
  // configured, so this never blocks a deployment that has no keys yet.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const onToken = useCallback((token: string | null) => {
    setTurnstileToken(token);
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RequestInstituteInput>({
    resolver: zodResolver(requestInstituteSchema),
    defaultValues: {
      name: '',
      slug: '',
      owner_email: '',
      owner_name: '',
      owner_phone: '',
    },
  });

  const submit = useMutation({
    mutationFn: requestInstitute,
    onSuccess: (data) => {
      setServerError(null);
      setSuccess(data);
    },
    onError: (err: Error) => setServerError(err.message),
  });

  async function onSubmit(values: RequestInstituteInput): Promise<void> {
    setServerError(null);
    try {
      await submit.mutateAsync(
        turnstileToken === null ? values : { ...values, turnstile_token: turnstileToken },
      );
    } catch {
      /* onError set the message */
    }
  }

  return (
    <div className={s.root}>
      <nav className={s.topnav} aria-label="Site">
        <Link to="/" className={s.brand}>
          <span className={s.mark} aria-hidden="true">
            i
          </span>
          IELTS Mock
        </Link>
        <div className={s.topnavLinks}>
          <Link to="/" className={s.navLink}>
            Home
          </Link>
          <Link to="/login" className={s.navLink}>
            Log in
          </Link>
        </div>
      </nav>

      <main className={s.main}>
        <div className={s.card}>
          {success ? (
            <SuccessState result={success} />
          ) : (
            <>
              <header className={s.head}>
                <span className={s.eyebrow}>For coaching institutes</span>
                <h1 className={s.title}>Register your institute</h1>
                <p className={s.subtitle}>
                  Tell us a bit about your centre. We&apos;ll review your application and email your
                  owner account a sign-in link once approved.
                </p>
              </header>

              <form
                noValidate
                onSubmit={(event) => {
                  void handleSubmit(onSubmit)(event);
                }}
                className={s.form}
              >
                <div className={s.formFull}>
                  <Label htmlFor="name">Institute name</Label>
                  <Input
                    id="name"
                    autoComplete="organization"
                    placeholder="Acme IELTS Coaching"
                    invalid={errors.name !== undefined}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                    {...register('name')}
                  />
                  <FormError id="name-error" message={errors.name?.message} />
                </div>

                <div className={s.formFull}>
                  <Label htmlFor="slug">Workspace slug</Label>
                  <Input
                    id="slug"
                    autoComplete="off"
                    placeholder="acme-coaching"
                    invalid={errors.slug !== undefined}
                    aria-describedby={errors.slug ? 'slug-error' : 'slug-help'}
                    {...register('slug')}
                  />
                  {errors.slug !== undefined ? (
                    <FormError id="slug-error" message={errors.slug.message} />
                  ) : (
                    <p id="slug-help" className={s.hint}>
                      Used in invitation links. Lowercase letters, digits and hyphens only.
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="owner_name">Owner name</Label>
                  <Input
                    id="owner_name"
                    autoComplete="name"
                    placeholder="Alice Sharma"
                    invalid={errors.owner_name !== undefined}
                    aria-describedby={errors.owner_name ? 'owner-name-error' : undefined}
                    {...register('owner_name')}
                  />
                  <FormError id="owner-name-error" message={errors.owner_name?.message} />
                </div>

                <div>
                  <Label htmlFor="owner_email">Owner email</Label>
                  <Input
                    id="owner_email"
                    type="email"
                    autoComplete="email"
                    placeholder="owner@institute.edu"
                    invalid={errors.owner_email !== undefined}
                    aria-describedby={errors.owner_email ? 'owner-email-error' : undefined}
                    {...register('owner_email')}
                  />
                  <FormError id="owner-email-error" message={errors.owner_email?.message} />
                </div>

                <div className={s.formFull}>
                  <Label htmlFor="owner_phone">Owner mobile number</Label>
                  <Controller
                    control={control}
                    name="owner_phone"
                    render={({ field }) => (
                      <PhoneField
                        id="owner_phone"
                        value={field.value}
                        onChange={field.onChange}
                        invalid={errors.owner_phone !== undefined}
                        describedBy={errors.owner_phone ? 'owner-phone-error' : 'owner-phone-help'}
                      />
                    )}
                  />
                  {errors.owner_phone !== undefined ? (
                    <FormError id="owner-phone-error" message={errors.owner_phone.message} />
                  ) : (
                    <p id="owner-phone-help" className={s.hint}>
                      We&apos;ll never share this number.
                    </p>
                  )}
                </div>

                {serverError !== null ? (
                  <div className={s.formFull}>
                    <StatusBanner tone="error">{serverError}</StatusBanner>
                  </div>
                ) : null}

                {/* Renders nothing when no site key is configured, so this
                    form behaves exactly as it did before Phase 184 until
                    somebody creates one. */}
                <div className={s.formFull}>
                  <Turnstile onToken={onToken} />
                </div>

                <div className={`${s.formFull} ${s.formActions}`}>
                  <Button type="submit" loading={isSubmitting || submit.isPending}>
                    Submit application
                  </Button>
                  <p className={s.legal}>
                    By submitting you confirm you are authorised to register this institute and
                    agree to our acceptable-use terms.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function SuccessState({ result }: { result: RequestInstituteResult }): JSX.Element {
  return (
    <div className={s.success}>
      <span className={s.successIcon} aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 11.5l4.5 4.5L18 6.5" />
        </svg>
      </span>
      <h1 className={s.successTitle}>Application received</h1>
      <p className={s.successBody}>
        {result.already_existed
          ? 'We already have a pending application for this workspace. Our team will be in touch with the owner email shortly.'
          : 'Thanks — your application is in review. We sent a sign-in invite to the owner email; once we approve your institute you can complete sign-in and start adding students.'}
      </p>
      <div className={s.successActions}>
        <Link to="/" className={s.successPrimary}>
          Back to home
        </Link>
        <Link to="/login" className={s.successSecondary}>
          Already invited? Log in
        </Link>
      </div>
    </div>
  );
}
